import { useState, useEffect, useRef } from 'react';
import nstyles from './NovaChat.module.css';
import ReactMarkdown from 'react-markdown';

export default function NovaChat({
  apiUrl,
  apiKey,
  domain,
  companyId,
  userName,
  userId
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [showForm, setShowForm] = useState(true);
  const [userData, setUserData] = useState({
    consulta: ''
  });
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [chatId, setChatId] = useState(null);
  const [isStartingChat, setIsStartingChat] = useState(false);
  const [error, setError] = useState(null);
  const chatBodyRef = useRef(null);
  const messageIdCounterRef = useRef(0);

  const [npsQuestion, setNpsQuestion] = useState(null);
  const [npsQuestionId, setNpsQuestionId] = useState(null);
  const [showNpsSurvey, setShowNpsSurvey] = useState(false);
  const [selectedNpsScore, setSelectedNpsScore] = useState(null);
  const [isSubmittingNps, setIsSubmittingNps] = useState(false);
  const [npsSubmitted, setNpsSubmitted] = useState(false);
  const [npsError, setNpsError] = useState(null);

  const getNextMessageId = () => {
    messageIdCounterRef.current += 1;
    return messageIdCounterRef.current;
  }
  
  const handleNpsScoreSelect = async (score) => {
    if (isSubmittingNps || !npsQuestionId || !chatId) {
      console.warn("NPS submit prevented:", {isSubmittingNps, npsQuestionId, chatId});
      return;
    }

    setSelectedNpsScore(score);
    setIsSubmittingNps(true);
    setNpsError(null);

    console.log(`NPS Score selected: ${score}. Submitting...`);
    
    try {
      const response = await fetch(`${apiUrl}/nps/saveNPSAnswer`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${apiKey}`,
          'Origin': domain || window.location.origin
        },
        body: JSON.stringify({
          channel_name: 'widget-chat',
          company_id: companyId,
          name: userName || 'Usuario Chat',
          client_id: userId || chatId || 'unknown',
          question_id: npsQuestionId,
          answer: score.toString()
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || `Error ${response.status} al guardar NPS`);
      }

      console.log("NPS Answer submitted successfully:", data);
      setNpsSubmitted(true);
      setShowNpsSurvey(false);

    } catch (err) {
      console.error("Error submitting NPS answer:", err);
      setNpsError(err.message || "No se pudo guardar tu respuesta. Intenta de nuevo.");
    } finally {
      setIsSubmittingNps(false);
    }
  };

  const toggleWidget = () => {
    const closing = isOpen;
    setIsOpen(!isOpen);

    console.log("Toggling widget. Closing:", closing, "Has question:", !!npsQuestion, "Submitted:", npsSubmitted, "Chat ID Exists:", !!chatId);
    if (closing && npsQuestion && !npsSubmitted && chatId !== null) {
      
      setShowNpsSurvey(true);
      setSelectedNpsScore(null);
      setNpsError(null);
      console.log("Showing NPS survey because chat exists.");
    } else {
      
      setShowNpsSurvey(false);
      console.log("Hiding NPS survey. Reason:", { opening: !closing, noQuestion: !npsQuestion, alreadySubmitted: npsSubmitted, noChatId: !chatId });
    }
  };

  
  const startChat = async (initialMessage) => {
    setIsStartingChat(true);
    setError(null);
    console.log("Attempting to start chat with:", initialMessage);

    try {
      const response = await fetch(`${apiUrl}/chats-company/start`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${apiKey}`,
          'Origin': domain || window.location.origin
        },
        body: JSON.stringify({ initialMessage })
      });

      if (!response.ok) {
        let errorMessage = `Error ${response.status}: No se pudo iniciar el chat.`;
        try {
          const contentType = response.headers.get("content-type");
          if (contentType && contentType.includes("application/json")) {
            const errorData = await response.json();
            errorMessage = errorData.message || errorMessage;
          } else {
            const errorText = await response.text();
            console.error("Error response (text) from /chats-company/start:", errorText);
          }
        } catch (e) {
          console.error("Could not parse error response from /chats-company/start:", e);
        }
        console.error("Full error details:", { status: response.status, statusText: response.statusText });  //** */
        throw new Error(errorMessage);
      }

      const data = await response.json();

      if (!data.chat_id) {
         console.error("No chat_id received from /chats-company/start");
         throw new Error('No se recibió un ID de chat válido.');
      }

      console.log("Chat started successfully, chatId:", data.chat_id);
      setChatId(data.chat_id);

      
      const userInitialMessageId = getNextMessageId();
      const assistantWelcomeMessageId = getNextMessageId();

      setMessages([
        {
          role: 'user',
          content: initialMessage || 'Hola, me gustaría iniciar una conversación.',
          messageId: userInitialMessageId
        },
        {
          role: 'assistant',
          content: `Hola ${userName}, en breve respondo tu duda.`,
          messageId: assistantWelcomeMessageId,
          loading: false
        }
      ]);

      setShowForm(false);
      await sendMessage(initialMessage, data.chat_id);

    } catch (err) {
      console.error('Error starting chat:', err);
      setError(err.message || 'Ocurrió un error al iniciar el chat.');
      
    } finally {
      setIsStartingChat(false);
    }
  };

  
  const handleFormSubmit = (e) => {
    e.preventDefault();
    const initialQuery = userData.consulta.trim();
    if (!initialQuery) {
      setError("Por favor, ingresa tu consulta inicial.");
      return;
    }
    
    startChat(initialQuery); 
  };

  const handleInputChange = (e) => {
    setUserData({ consulta: e.target.value });
  };

  const handleChatInputChange = (e) => {
    setInputMessage(e.target.value);
  };

  
  const handleSendMessage = () => {
    const messageToSend = inputMessage.trim();
    if (messageToSend === '' || !chatId) {
      if(!chatId) console.error("Cannot send message, chatId is null");
      return;
    }

    
    const newUserMessage = {
      role: 'user',
      content: messageToSend,
      messageId: getNextMessageId()
    };
    setMessages(prev => [...prev, newUserMessage]);
    setInputMessage('');

    
    sendMessage(messageToSend, chatId);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSendMessage();
    }
  };

  
  const sendMessage = async (message, currentChatId) => {
    setError(null); 

    
    const loadingMessageId = getNextMessageId();
    const loadingMessage = {
      role: 'assistant',
      content: '',
      loading: true,
      messageId: loadingMessageId
    };
    setMessages(prev => [...prev, loadingMessage]);

    try {
      const response = await fetch(`${apiUrl}/chats-company/process`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${apiKey}`,
          'Origin': domain || window.location.origin
        },
        body: JSON.stringify({
          messages: [{ role: 'user', content: message }], 
          chat_id: currentChatId
        })
      });

      
      if (!response.ok) {
        
         let errorMsg = `Error ${response.status}`;
         try {
           const contentType = response.headers.get("content-type");
           if (contentType && contentType.includes("application/json")) {
             const errorData = await response.json();
             errorMsg = errorData.message || errorMsg;
           } else {
             const errorText = await response.text();
             console.error(`Error response (text) from /chats-company/process (${response.status}):`, errorText);
           }
         } catch (e) {
           console.error("Could not parse error response from /chats-company/process:", e);
         }
         console.error("Full error details:", { status: response.status, statusText: response.statusText });
         throw new Error(errorMsg);
      }

      if (!response.body) {
        throw new Error('No response body available');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let assistantMessageContent = '';
      let assistantPdfSources = [];
      let receivedError = null; 

      
      setMessages(prev => {
         const filtered = prev.filter(msg => msg.messageId !== loadingMessageId);
         filtered.push({
           role: 'assistant',
           content: '',
           loading: false,
           messageId: loadingMessageId,
           pdfSources: []
         });
         return filtered;
      });


      while (true) {
        const { done, value } = await reader.read();
        if (done) {
           console.log("Stream finished.");
           break;
        }

        buffer += decoder.decode(value, { stream: true });
        
        let boundary = buffer.indexOf('\n\n');
        while (boundary !== -1) {
           const event = buffer.substring(0, boundary);
           buffer = buffer.substring(boundary + 2);

           if (event.startsWith('data:')) {
              const jsonStr = event.slice(5).trim();
              if (jsonStr === '[DONE]') { 
                 console.log("Received [DONE] event.");
                 
                 continue; 
              }

              try {
                 const parsed = JSON.parse(jsonStr);

                 
                 if (parsed.error) {
                   console.error("Error received in stream:", parsed.error);
                   receivedError = parsed.error.message || 'Error desconocido en el stream.';
                   
                   setMessages(prev => prev.map(msg => 
                     msg.messageId === loadingMessageId 
                     ? { ...msg, content: `Error: ${receivedError}`, loading: false, isError: true } 
                     : msg
                   ));
                   reader.cancel();
                   break;
                 }

                 
                 if (parsed.message) {
                    if (parsed.message.content) {
                       assistantMessageContent += parsed.message.content;
                    }
                    
                    if (parsed.message.pdfSources && parsed.message.pdfSources.length > 0) {
                        
                       assistantPdfSources = parsed.message.pdfSources; 
                    }
                    
                    
                    setMessages(prev => prev.map(msg =>
                       msg.messageId === loadingMessageId
                         ? { ...msg, content: assistantMessageContent, pdfSources: assistantPdfSources }
                         : msg
                    ));
                 }
              } catch (e) {
                 console.error('Error parsing JSON from stream:', e, 'Raw data:', jsonStr);
                 
              }
           }
           boundary = buffer.indexOf('\n\n'); 
        } 
         if(receivedError) break;
      } 

       if (receivedError) {
          
          setError(receivedError); 
       }


    } catch (error) {
      console.error('Error sending/receiving message:', error);
      
      const messageContentForBubble = error?.message || 'Ocurrió un error inesperado.';
      
      setError(messageContentForBubble); 

      
      setMessages(prev => {
         const updatedMessages = prev.map(msg => 
             msg.messageId === loadingMessageId 
             ? { ...msg, content: messageContentForBubble, loading: false, isError: true }
             : msg
         );
         
         if (!updatedMessages.some(msg => msg.messageId === loadingMessageId)) {
             updatedMessages.push({
                 role: 'system',
                 content: messageContentForBubble,
                 messageId: loadingMessageId,
                 isError: true,
             });
         }
         return updatedMessages;
      });
    }
  };

  
  useEffect(() => {
    if (chatBodyRef.current) {
      chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight;
    }
  }, [messages]);

  
  useEffect(() => {
    const fetchNpsQuestion = async () => {
      if (!companyId) {
        console.warn("NPS: companyId is required but not provided.");
        return;
      }
      try {
        const response = await fetch(`${apiUrl}/nps/getQuestionByChannelAndCompany?channel_name=widget-chat&company_id=${companyId}`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${apiKey}`,
            'Origin': domain || window.location.origin
          }
        });
        const data = await response.json();
        if (!response.ok) {
          throw new Error(data.message || 'Error fetching NPS question');
        }
        if (data && data.question && data.id) {
          setNpsQuestion(data.question);
          setNpsQuestionId(data.id.toString());
          console.log("NPS Question loaded:", data.question);
        } else {
          console.warn("NPS question data received is invalid:", data);
        }
      } catch (err) {
        console.error("Error fetching NPS question:", err);
        setNpsError("No se pudo cargar la pregunta de satisfacción.");
      }
    };

    fetchNpsQuestion();
    
  }, [apiUrl, apiKey, companyId, domain]);

  
  return (
    <> {}

      {}
      {!isOpen ? (
        
        !showNpsSurvey && (
            <div className={nstyles.widgetClosed} onClick={toggleWidget}>
               {}
            </div>
        )
      ) : (
        
        <div className={nstyles.widgetOpen}>
          <div className={nstyles.chatHeader}>
            <img src="/LOGO-INLEARNING.webp" alt="Inlearning Logo" className={nstyles.chatLogo} />
            <button className={nstyles.closeBtn} onClick={toggleWidget}>&times;</button>
          </div>

          {error && <div className={nstyles.errorMessage}>{error}</div>}

          {showForm ? (
            <div className={nstyles.formContainer}>
              <form id="user-form" onSubmit={handleFormSubmit}>
                <div className={nstyles.formGroup}>
                  <label htmlFor="consulta">Consulta Inicial</label>
                  <input type="text" id="consulta" required value={userData.consulta} onChange={handleInputChange} placeholder="¿En qué podemos ayudarte?" disabled={isStartingChat}/>
                </div>
                <button type="submit" disabled={isStartingChat}>
                  {isStartingChat ? 'Iniciando...' : 'Iniciar Chat'}
                </button>
              </form>
            </div>
          ) : (
            <>
              <div className={nstyles.chatBody} ref={chatBodyRef}>
                {messages.map((message) => (
                  <div key={message.messageId} className={`${message.role === 'user' ? nstyles.userMessage : message.role === 'assistant' ? nstyles.assistantMessage : nstyles.systemMessage} ${message.isError ? nstyles.errorMessageText : ''}`}>
                    {message.loading ? (<div className={nstyles.loadingDots}><span></span><span></span><span></span></div>) : (
                      <div className={nstyles.markdownContent}>
                        <ReactMarkdown>{message.content}</ReactMarkdown>
                      </div>
                    )}
                    {message.role === 'assistant' && message.pdfSources && message.pdfSources.length > 0 && (
                      <div className={nstyles.pdfSources}>
                        <strong>Fuentes:</strong>
                        <ul>{message.pdfSources.map((source, index) => (<li key={index}><a href={source.downloadUrl} target="_blank" rel="noopener noreferrer">{source.name} (Páginas: {source.pages.join(', ')})</a></li>))}</ul>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <div className={nstyles.chatFooter}>
                <input type="text" id="chat-input" placeholder="Escribe tu mensaje..." value={inputMessage} onChange={handleChatInputChange} onKeyPress={handleKeyPress} disabled={!chatId}/>
                <button onClick={handleSendMessage} disabled={!chatId || inputMessage.trim() === ''}>Enviar</button>
              </div>
            </>
          )}
        </div>
      )} {}

      {}
      {showNpsSurvey && npsQuestion && (
        <div className={nstyles.npsSurveyContainer}>
          <h4>{npsQuestion}</h4>
          {npsError && <div className={nstyles.npsErrorMessage}>{npsError}</div>}
          <div className={nstyles.npsScoreButtons}>
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((score) => (
              <button
                key={score}
                className={`${nstyles.npsScoreButton} ${selectedNpsScore === score ? nstyles.selected : ''}`}
                onClick={() => handleNpsScoreSelect(score)}
                disabled={isSubmittingNps}
              >
                {score}
              </button>
            ))}
          </div>
           {isSubmittingNps && <div className={nstyles.npsLoading}>Guardando...</div>}
           <button
             className={nstyles.npsCloseButton}
             onClick={() => setShowNpsSurvey(false)}
             disabled={isSubmittingNps}
             aria-label="Cerrar encuesta"
           >
             &times;
           </button>
        </div>
      )}

    </> 
  );
}


export function useNovaChat(options) {
  useEffect(() => {
    console.log("Widget NovaChat cargado con opciones:", options);
  }, [options]);
  return null;
}

export function init(options) {
  console.log("Widget NovaChat inicializado globalmente con opciones:", options);
  
  return options;
}
